<div
  class="card mb-5 gantt-card"
  [class.hide-header]="data?.hideHeader"
  [style.minHeight]="data?.hideHeader ? null : resolvedCardHeight"
>
  <div class="card-header" style="border-bottom: none;" *ngIf="!data?.hideHeader">
    <div class="card-title">
      <div class="title-block">
        <h4>{{ data?.title }}</h4>
        <p class="timeline-range" *ngIf="rangeLabel">
          {{ rangeLabel }}
          <span *ngIf="taskCount" class="timeline-divider">•</span>
          <span *ngIf="taskCount">{{ taskCount }} items</span>
        </p>
      </div>
      <span class="timeline-mode">{{ timelineLabel }}</span>
    </div>
  </div>

  <div class="card-body" [class.full-chart-body]="data?.hideHeader">
    <div #fullscreenContainer class="gantt-fullscreen-container" [class.fullscreen-active]="isFullscreen">
      <div class="gantt-legend" *ngIf="legendItems.length">
        <div class="legend-filter-group">
          <p class="legend-filter-title">Strategic Drivers</p>
          <div class="legend-filter-items">
            <button
              type="button"
              class="gantt-legend-item"
              [class.active]="isLegendActive(item.label)"
              [class.inactive]="!isLegendActive(item.label)"
              [attr.aria-pressed]="isLegendActive(item.label)"
              *ngFor="let item of legendItems"
              (click)="onLegendClick(item.label)"
            >
              <span class="gantt-legend-swatch" [style.background]="item.color"></span>
              <span class="gantt-legend-label">{{ item.label }}</span>
              <span class="legend-toggle-state">{{ isLegendActive(item.label) ? 'On' : 'Off' }}</span>
            </button>
          </div>
          <div class="legend-row-action">
            <button
              type="button"
              class="fullscreen-toggle"
              [attr.aria-label]="isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'"
              [attr.title]="isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'"
              (click)="toggleFullscreen()"
            >
              <i [class]="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div class="legend-filter-group with-action" *ngIf="data?.chartConfig?.showLineStyleLegend">
          <p class="legend-filter-title">Activity Types</p>
          <div class="legend-filter-items">
            <button
              type="button"
              class="gantt-legend-item gantt-style-legend-item"
              [class.active]="isLineStyleLegendActive('solid')"
              [class.inactive]="!isLineStyleLegendActive('solid')"
              [attr.aria-pressed]="isLineStyleLegendActive('solid')"
              (click)="onLineStyleLegendClick('solid')"
            >
              <span class="gantt-style-line solid"></span>
              <span class="gantt-legend-label">
                {{ data?.chartConfig?.solidLineLegendLabel || 'Solid line' }}
              </span>
              <span class="legend-toggle-state">{{ isLineStyleLegendActive('solid') ? 'On' : 'Off' }}</span>
            </button>

            <button
              type="button"
              class="gantt-legend-item gantt-style-legend-item"
              [class.active]="isLineStyleLegendActive('dotted')"
              [class.inactive]="!isLineStyleLegendActive('dotted')"
              [attr.aria-pressed]="isLineStyleLegendActive('dotted')"
              (click)="onLineStyleLegendClick('dotted')"
            >
              <span class="gantt-style-line dotted"></span>
              <span class="gantt-legend-label">
                {{ data?.chartConfig?.dottedLineLegendLabel || 'Dotted line' }}
              </span>
              <span class="legend-toggle-state">{{ isLineStyleLegendActive('dotted') ? 'On' : 'Off' }}</span>
            </button>
          </div>
        </div>
      </div>

      <div
        class="gantt-layout"
        [class.hidden]="hasError"
        [class.with-side-tree]="isSideTreeVisible()"
        [style.--gantt-side-tree-min]="resolveSideTreeMinWidth()"
        [style.--gantt-side-tree-max]="resolveSideTreeMaxWidth()"
      >
      <button
        *ngIf="isFullscreen && showSideTree"
        type="button"
        class="side-pane-arrow"
        [class.collapsed]="isSideTreeCollapsed"
        [attr.aria-label]="isSideTreeCollapsed ? 'Expand tree panel' : 'Collapse tree panel'"
        (click)="toggleSideTreeCollapse()"
      >
        {{ isSideTreeCollapsed ? '❯' : '❮' }}
      </button>
      <div class="gantt-side-tree" *ngIf="isSideTreeVisible()" [style.height]="resolvedHeight">
        <div class="tree-header">
          <span class="tree-header-label">Segments</span>
          <button
            type="button"
            class="fullscreen-toggle chart-info-toggle tree-info-toggle"
            aria-label="Chart information"
            (mouseenter)="showChartInfoTooltip = true"
            (mouseleave)="showChartInfoTooltip = false"
            (focus)="showChartInfoTooltip = true"
            (blur)="showChartInfoTooltip = false"
          >
            <i class="fas fa-info-circle" aria-hidden="true"></i>
          </button>
          <div class="tree-info-tooltip" *ngIf="showChartInfoTooltip">
            {{ chartInfoTooltip }}
          </div>
        </div>

        <div class="tree-groups">
          <div class="tree-segment" *ngFor="let segment of sideTree">
            <button
              type="button"
              class="tree-toggle segment"
              [style.--segment-bg]="resolveTreeSegmentColor(segment)"
              [class.active]="isSegmentFocused(segment)"
              [attr.aria-pressed]="isSegmentFocused(segment)"
              (click)="toggleSegmentSelection(segment)"
            >
              <span
                class="tree-caret"
                [attr.aria-label]="segment.collapsed ? 'Expand segment' : 'Collapse segment'"
                tabindex="0"
                (click)="$event.stopPropagation(); toggleSegmentCollapse(segment)"
                (keydown.enter)="$event.stopPropagation(); toggleSegmentCollapse(segment)"
                (keydown.space)="$event.preventDefault(); $event.stopPropagation(); toggleSegmentCollapse(segment)"
              >
                {{ segment.collapsed ? '+' : '-' }}
              </span>
              <span class="tree-label">{{ segment.label }}</span>
              <span class="tree-count" [attr.aria-label]="segment.count + ' projects'">
                {{ segment.count }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="chart-frame" [style.height]="resolvedHeight">
        <div class="timeline-header" *ngIf="timelineHeaderMarkers.length">
          <div
            class="timeline-header-track"
            [style.width]="resolvedChartContentWidth"
            [style.transform]="'translateX(-' + timelineHeaderScrollLeft + 'px)'"
          >
            <span
              class="timeline-header-marker"
              *ngFor="let marker of timelineHeaderMarkers"
              [style.left.px]="marker.left"
            >
              {{ marker.label }}
            </span>
          </div>
        </div>

        <div
          #chartShell
          class="chart-shell"
          [style.height]="resolvedChartBodyHeight"
          (scroll)="onChartShellScroll()"
        >
          <div
            #chartHost
            [id]="data?.widgetId"
            class="chart-host"
            [style.height]="resolvedChartContentHeight"
            [style.width]="resolvedChartContentWidth"
          ></div>
        </div>
      </div>
      </div>
    </div>
    <div class="chart-error" *ngIf="hasError" [style.minHeight]="resolvedHeight">{{ errorMessage }}</div>
  </div>
</div>
